import * as React from "react";
import * as DTO from "../../../interfaces/dtos";
import { RouteComponentProps } from "react-router-dom";
import { Button, Grid } from "semantic-ui-react";

import { TitleSection, SideBar } from "@/components";

import { WhiteContainer } from "@/styling/baseStyle";

import "./styles.scss";

import { VideoScriptsServices } from "@/services";
import { useCallback, useState } from "react";
import { VideoScriptForm } from "./components/VideoScriptForm";
import { VideoScriptRequest } from "@/interfaces";
import { URLS, useDeleteVideoScript, useSaveVideoScript } from "@/utils";
import { AppContext } from "@/providers";
import { NotificationModal } from "./components/NotificationModal";
import { toast } from "sonner";

interface MatchParams {
  id: string;
}
type Props = RouteComponentProps<MatchParams>;

const VideoScriptFC: React.FC<Props> = ({ history, match: { params } }) => {
  const { userContext } = React.useContext(AppContext);
  const [videoScript, setVideoScript] = useState<DTO.VideoScript>();
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [values, setValues] = useState<any>();
  const isNew = params["id"] === "new";

  const { mutateAsync: deleteAsync } = useDeleteVideoScript();
  const { mutateAsync: saveAsync } = useSaveVideoScript();

  React.useEffect(() => {
    const getScript = async (scriptId: number | string) => {
      const result = await VideoScriptsServices.get(scriptId);
      setVideoScript(result);
    };

    if (params["id"] && !isNew) {
      getScript(params["id"]).catch(console.error);
    } else {
      setVideoScript({
        playerId: userContext?.player?.id!!,
        title: "",
        content: "",
        categories: [],
        industryRoles: [],
      });
    }
  }, []);

  const deleteScript = useCallback(async () => {
    if (window.confirm("Are you sure you want to delete this script?")) {
      try {
        setLoading(true);
        await deleteAsync(videoScript?.videoScriptId!!);
        history.push(URLS.player.videoScripts);
        toast.success(`Script deleted`);
      } catch (e: any) {
        console.error(e);
        toast.error(`There was an error deleting the script`, { description: e.message || "Unknown error" });
      } finally {
        setLoading(false);
      }
    }
  }, [videoScript?.videoScriptId, deleteAsync, history]);

  const saveScript = useCallback(async () => {
    try {
      setLoading(true);
      const request: VideoScriptRequest = {
        playerId: values.playerId,
        title: values.title,
        content: values.content,
        categories: [],
        industryRoles: [],
      };

      const updatedScript = await saveAsync({
        id: videoScript?.videoScriptId,
        request,
      });

      setVideoScript(updatedScript);
      if (updatedScript.videoScriptId != values?.videoScriptId) {
        history.replace(URLS.player.videoScript.replace(":id", `${updatedScript.videoScriptId}`));
      }
      toast.success(`Script saved`);
    } catch (e: any) {
      console.error(e);
      toast.error(`There was an error saving the script`, { description: e.message || "Unknown error" });
    } finally {
      setLoading(false);
    }
  }, [values, saveAsync, history]);

  return (
    <WhiteContainer>
      <SideBar history={history} />
      <div className={"hugePadding videoScriptPage"}>
        <TitleSection
          title={"Video Script"}
          backLabel={"Back to Script Library"}
          handleBackAction={() => {
            history.replace(URLS.player.videoScripts);
          }}
          showNextOption={!isNew}
          showLinkOption={true}
          customNextLabel={"Record Video"}
          handleNextAction={() =>
            history.push(URLS.player.recordVideo, { script: values?.content, id: videoScript?.videoScriptId })
          }
          disableButtons={!isValid}
          titleSize={6}
          buttonsSize={9}
          nextButtonSize={5}
          customLinkSection={
            <Grid.Column width={10}>
              <Grid columns={2}>
                {videoScript?.videoScriptId && videoScript?.playerId !== undefined && (
                  <Grid.Column>
                    <Button
                      type="submit"
                      data-elm-id={"deleteVideoScript"}
                      loading={loading}
                      fluid
                      className={"secondary rounded buttonRight"}
                      onClick={deleteScript}
                    >
                      Delete
                    </Button>
                  </Grid.Column>
                )}
                {(videoScript?.playerId || videoScript?.videoScriptId == undefined) && (
                  <Grid.Column>
                    <Button
                      type="submit"
                      data-elm-id={"scriptNotification"}
                      loading={loading}
                      fluid
                      className={"primary rounded buttonRight noPaddingHorizontal"}
                      onClick={saveScript}
                      disabled={!isValid}
                    >
                      Save
                    </Button>
                  </Grid.Column>
                )}
              </Grid>
            </Grid.Column>
          }
        />
        <Grid columns={2}>
          <Grid.Column>
            <Grid.Row className={"videoScriptFormContainer"}>
              <Grid.Column>
                {videoScript && (
                  <VideoScriptForm
                    videoScript={videoScript}
                    updatingHandler={setValues}
                    validationHandler={setIsValid}
                    disabled={false}
                  />
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid.Column>
        </Grid>

        {showNotificationModal && videoScript && (
          <NotificationModal
            opened={showNotificationModal}
            script={videoScript}
            acceptHandler={() => setShowNotificationModal(false)}
            rejectHandler={() => setShowNotificationModal(false)}
          />
        )}
      </div>
    </WhiteContainer>
  );
};

export const PlayerVideoScriptPage = VideoScriptFC;
